const baseUrl = process.env.REACT_APP_PRODUCT_SERVICE_API_URL;
const bearerToken = process.env.REACT_APP_PRODUCT_SERVICE_API_TOKEN;

const productServiceApi = async (path, { headers, ...options } = {}) =>
  window.fetch(`${baseUrl}${path}`, {
    headers: {
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      ...headers,
    },
    ...options,
  });

export const fetchProductDataBySku = async sku => {
  const response = await productServiceApi(`/product/${sku}`);
  const data = await response.json();
  return data;
};

const FIELDS_MAPPING = [
  { source: 'skuWithSize', target: 'sku' },
  { source: 'title', target: 'title' },
  {
    source: 'description',
    target: 'description',
    mapDisplay: (_, { source: { description, material } }, intl) =>
      [
        description &&
          intl.formatMessage({ id: 'ListingField.description.line1.skuMapping' }, { description }),
        material &&
          intl.formatMessage({ id: 'ListingField.description.line2.skuMapping' }, { material }),
      ]
        .filter(Boolean)
        .join('\n\n'),
  },
  {
    source: 'material',
    target: 'material',
    mapValue: values =>
      values?.split(',').map(value => {
        const [, material] = /^[ ]?[0-9]+% (.*)$/i.exec(value) || [];
        return material?.toLowerCase();
      }),
  },
  {
    source: 'color',
    target: 'kolor',
    mapValue: (value, target, intl) =>
      intl.messages[`ListingField.${target}.${value?.toLowerCase()}.skuMapping`] || value,
  },
  {
    source: 'skuSize',
    target: 'rozmiar_.*',
    mapValue: (value, target, intl) =>
      intl.messages[`ListingField.${target}.${value?.toLowerCase()}.skuMapping`] || value,
  },
  {
    source: 'photo_url',
    target: 'images',
    mapValue: value =>
      value
        ? [
            value.replace(
              /(^https?:\/\/www.reserved.com\/(pl\/pl\/)?media\/catalog\/product\/)|(^\/)/g,
              'https://static.reserved.com/media/catalog/product/'
            ),
          ]
        : [],
  },
];

export const mapProductDataToListingInitialValues = async ({
  productData,
  config,
  intl,
  onImageUpload,
}) => {
  const {
    layout: { listingImage },
    listing: { listingFields },
  } = config;

  const fieldValues = await FIELDS_MAPPING.reduce(
    async (fields, { source, target, mapValue, mapDisplay }) => {
      const sourceValue = productData[source];
      const targetRegex = new RegExp(`^(${target})$`, 'i');
      const targetFields = listingFields.filter(({ key }) => targetRegex.test(key));

      let sourceKey = source;
      let targetKey = target;
      let targetValue = mapValue ? mapValue(sourceValue, target, intl) : sourceValue;

      if (targetKey === 'images') {
        targetValue = (await Promise.allSettled(
          targetValue.map(async url => {
            const fileResponse = await window.fetch(url);
            const file = await fileResponse.blob();
            const fileName = url.split('/').pop();
            const id = `${fileName}_${Date.now()}`;
            const image = await onImageUpload({ id, file }, listingImage);
            return image;
          })
        ))
          .filter(({ status }) => status === 'fulfilled')
          .map(({ value }) => value);

        return [
          ...(await fields),
          {
            sourceKey,
            sourceValue,
            targetKey,
            targetValue,
            mapDisplay,
          },
        ];
      }

      if (!targetFields.length) {
        return [
          ...(await fields),
          {
            sourceKey,
            sourceValue,
            targetKey,
            targetValue,
            mapDisplay,
          },
        ];
      }

      return [
        ...(await fields),
        ...targetFields.map(({ key, schemaType, scope, enumOptions }) => {
          const isMulti = schemaType === 'multi-enum';
          const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
          targetKey = `${namespacePrefix}${key}`;
          targetValue = mapValue ? mapValue(sourceValue, key, intl) : sourceValue;

          if (!enumOptions) {
            return {
              sourceKey,
              sourceValue,
              targetKey,
              targetValue,
              mapDisplay,
            };
          }

          const filterOption = ({ label }) =>
            Array.isArray(targetValue)
              ? targetValue.includes(label.toLowerCase())
              : targetValue?.toLowerCase() === label.toLowerCase();
          const mapOption = ({ option } = {}) => option;
          targetValue = isMulti
            ? enumOptions.filter(filterOption).map(mapOption)
            : mapOption(enumOptions.find(filterOption));

          return {
            sourceKey,
            sourceValue,
            targetKey,
            targetValue,
            mapDisplay,
          };
        }),
      ];
    },
    []
  );

  const values = fieldValues.reduce(
    ({ source, target }, { sourceKey, sourceValue, targetKey, targetValue }) => ({
      source: { ...source, [sourceKey]: sourceValue },
      target: { ...target, [targetKey]: targetValue },
    }),
    {}
  );

  return fieldValues.reduce(
    (reduced, { targetKey, targetValue, mapDisplay }) => ({
      ...reduced,
      [targetKey]: mapDisplay ? mapDisplay(targetValue, values, intl) : targetValue,
    }),
    {}
  );
};

export default productServiceApi;
